import { useEffect, useMemo, useState } from "react";
import {
  CHALLENGE_CONSTRAINT_IDS,
  deserializeEditGraph,
  type ChallengeConstraintId,
  type EditGraph,
  type ScoreBreakdown,
} from "@desk/core";
import {
  buildChallengePayloadFromEdit,
  buildChallengeUrl,
  shareChallengeUrl,
  type ShareChallengeResult,
} from "../../game/challenge/url.js";
import { CONSTRAINT_LABELS } from "../../game/challenge/constraints.js";
import type { GamePresentation } from "../../game/challenge/presentation.js";
import type { PackId } from "../../content/registry.js";

export interface ShareChallengeButtonProps {
  packId: PackId;
  packVersion: string;
  seed: number;
  graphJson: string;
  score: Pick<
    ScoreBreakdown,
    "reach" | "contextLoss" | "claimDrift" | "credDelta" | "provenanceRisk"
  >;
  /** Defaults for re-share of an accepted challenge (or prior selection). */
  constraintIds?: readonly ChallengeConstraintId[];
  /**
   * Game-side presentation levers (effects / caption placement / precision-trim
   * / reaction) captured at publish, folded into the link's `?p=` sidecar so the
   * shared reel + "How it's landing" reproduce identically. Omitted / neutral =
   * no sidecar (link stays byte-identical to the core-only form).
   */
  presentation?: GamePresentation;
  className?: string;
  label?: string;
}

/** Allowed challenge constraint combinations for this slice. */
export function normalizeAuthoredConstraints(
  ids: readonly ChallengeConstraintId[],
): ChallengeConstraintId[] {
  const allowed = new Set<string>(CHALLENGE_CONSTRAINT_IDS);
  const out: ChallengeConstraintId[] = [];
  for (const id of ids) {
    if (!allowed.has(id)) continue;
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

export function ShareChallengeButton({
  packId,
  packVersion,
  seed,
  graphJson,
  score,
  constraintIds = [],
  presentation,
  className,
  label = "Beat My Edit",
}: ShareChallengeButtonProps) {
  const defaultsKey = constraintIds.join("|");
  const defaults = useMemo(
    () => normalizeAuthoredConstraints(constraintIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultsKey],
  );
  const [selected, setSelected] = useState<ChallengeConstraintId[]>(defaults);
  const [status, setStatus] = useState<"idle" | "copied" | "shared" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [lastUrl, setLastUrl] = useState<string | null>(null);

  useEffect(() => {
    setSelected(defaults);
  }, [defaults]);

  const toggle = (id: ChallengeConstraintId) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : normalizeAuthoredConstraints([...prev, id]),
    );
  };

  const onShare = async () => {
    setError(null);
    try {
      const graph: EditGraph = deserializeEditGraph(graphJson);
      const chosen = normalizeAuthoredConstraints(selected);
      const payload = buildChallengePayloadFromEdit({
        packId,
        packVersion,
        seed,
        graph,
        score,
        constraintIds: chosen,
      });
      const origin = typeof window !== "undefined" ? window.location.origin : "http://127.0.0.1";
      const path = typeof window !== "undefined" ? window.location.pathname : "/";
      const url = buildChallengeUrl(origin, path, payload, presentation);
      setLastUrl(url);
      const result: ShareChallengeResult = await shareChallengeUrl(url);
      if (result.ok && result.method === "web-share") {
        setStatus("shared");
      } else if (result.ok) {
        setStatus("copied");
      } else {
        setStatus("error");
        setError(result.error);
      }
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Share failed");
    }
  };

  return (
    <div className={className ?? "share-challenge"} data-testid="share-challenge">
      <div
        className="constraint-author constraint-chip-row"
        data-testid="constraint-author"
        role="group"
        aria-label="Challenge limits"
      >
        <button
          type="button"
          className={`constraint-chip-toggle${selected.length === 0 ? " active" : ""}`}
          data-testid="constraint-none"
          aria-pressed={selected.length === 0}
          onClick={() => setSelected([])}
        >
          No limits
        </button>
        {CHALLENGE_CONSTRAINT_IDS.map((id) => (
          <button
            key={id}
            type="button"
            className={`constraint-chip-toggle${selected.includes(id) ? " active" : ""}`}
            data-testid={`constraint-${id}`}
            aria-pressed={selected.includes(id)}
            onClick={() => toggle(id)}
          >
            {CONSTRAINT_LABELS[id]}
            {id === "deadline-bonus" ? " (soft)" : ""}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="secondary"
        data-testid="beat-my-edit"
        onClick={() => void onShare()}
      >
        {label}
      </button>
      {status === "copied" && (
        <p className="share-status ok" data-testid="share-copied">
          Challenge link copied
        </p>
      )}
      {status === "shared" && (
        <p className="share-status ok" data-testid="share-shared">
          Shared
        </p>
      )}
      {status === "error" && (
        <p className="share-status error" data-testid="share-error">
          {error ?? "Could not share"}
        </p>
      )}
      {lastUrl && (
        <p className="share-url-hint muted" data-testid="challenge-url-preview">
          Link is canonical · PNG/video is not byte-identical
        </p>
      )}
    </div>
  );
}
